import { useState, useEffect } from "react";

import { useApolloClient } from "react-apollo";
import gql from "graphql-tag";

import moment from "moment";

import { ALL_DIRECTIONS_QUERY } from "../graphql/Directions/Directions.graphql";
import { CURRENT_USER_QUERY } from "../graphql/Users/Users.graphql";
import { ALL_ORDERS_QUERY } from "../graphql/Orders/Orders.graphql";
import { SINGLE_TARIFF_QUERY } from "../graphql/Tariffs/Tariffs.graphql";
import { SINGLE_BOOKING_CODE_QUERY } from "../graphql/BookingCodes/BookingCodes.graphql";
import { ourToast as toast } from ".";

export const useLocalStorage = (key, initialValue, localStorage = true) => {
  const storageMethod = localStorage ? "localStorage" : "sessionStorage";
  const [storedValue, setStoredValue] = useState(() => {
    try {
      const item = window[storageMethod].getItem(key);
      return item ? JSON.parse(item) : initialValue;
    } catch (error) {
      console.log(error);
      return initialValue;
    }
  });
  const setValue = value => {
    try {
      const valueToStore =
        value instanceof Function ? value(storedValue) : value;
      setStoredValue(valueToStore);
      window[storageMethod].setItem(key, JSON.stringify(valueToStore));
    } catch (error) {
      console.log(error);
    }
  };

  const clearValue = () => {
    window[storageMethod].removeItem(key);
    setStoredValue(initialValue);
  };

  return [storedValue, setValue, clearValue];
};

export const useDirections = (startLocation, endLocation, priority = 1) => {
  const client = useApolloClient();
  const [directions, setDirections] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const { data: DirectionData } = await client.query({
          query: ALL_DIRECTIONS_QUERY,
          variables: {
            where: {
              OriginLocationRecIdRef: parseInt(startLocation),
              DestinationLocationRecIdRef: parseInt(endLocation),
              Priority_not: null
            },
            orderByMulti: ["Priority_ASC"],
            skip: 0,
            first: 1
          }
        });
        setDirections(DirectionData.Directions);
        setLoading(false);
      } catch (err) {
        toast.error("Error getting Directions.");
        setLoading(false);
      }
    };
    // if (startLocation && endLocation) {
    fetchData();
    // }
  }, [startLocation, endLocation, client, priority]);
  return {
    directions,
    loading
  };
};

export const useCurrentUser = () => {
  const client = useApolloClient();
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchCurrentUser = async () => {
      try {
        setLoading(true);
        const result = await client.query({
          query: CURRENT_USER_QUERY,
          fetchPolicy: "network-only"
        });
        if (result && result.data) {
          setData(result.data.Me);
        }
      } catch (err) {
        toast.error("Error getting Current User.");
      } finally {
        setLoading(false);
      }
    };
    fetchCurrentUser();
  }, [client]);
  return {
    data,
    loading
  };
};

export const useTodaysOrders = () => {
  const client = useApolloClient();
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchTodaysOrders = async () => {
      try {
        setLoading(true);
        const result = await client.query({
          query: ALL_ORDERS_QUERY,
          variables: {
            where: {
              PickupDate_gt: moment()
                .startOf("day")
                .toDate(),
              PickupDate_lt: moment()
                .endOf("day")
                .toDate()
            }
          },
          fetchPolicy: "network-only"
        });
        if (result && result.data) {
          setData(result.data.Orders);
        }
      } catch (err) {
        toast.error("Error getting Orders.");
      } finally {
        setLoading(false);
      }
    };
    fetchTodaysOrders();
  }, [client]);
  return {
    data,
    loading
  };
};

const COMPANY_PARAMETERS_QUERY = gql`
  query Companys($where: CompanyWhereInput!) {
    Companys(where: $where) {
      RecId
      NextOrderNo
      NextWONo
      NextInvcNo
      CompanyId
      DefTripInterval
      DefPickupLocationRecIdRef
      PrimaryStorageDimension {
        RecId
        CodeDescription
      }
      SecondaryStorageDimension {
        RecId
        CodeDescription
      }
      TertiaryStorageDimension {
        RecId
        CodeDescription
      }
      InventoryCostMethod {
        RecId
        CodeDescription
      }
      UsePrimaryStorageDimension
      UseSecondaryStorageDimension
      UseTertiaryStorageDimension
      AllowMaintenanceBilling
      LaborHourIncrement
    }
  }
`;

export const useCompanyParameters = () => {
  const CompanyId = window.localStorage.getItem("user_company");
  const client = useApolloClient();
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const {
          data: { Companys = [] }
        } = await client.query({
          query: COMPANY_PARAMETERS_QUERY,
          variables: {
            where: {
              CompanyId
            }
          },
          fetchPolicy: "network-only"
        });
        if (Companys[0]) {
          setData(Companys[0]);
        }
      } catch (err) {
        toast.error("Error getting Company Params.");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [CompanyId, client]);
  return {
    data,
    loading
  };
};

export const useTariff = TariffRecIdRef => {
  const client = useApolloClient();
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const {
          data: { Tariff = {} }
        } = await client.query({
          query: SINGLE_TARIFF_QUERY,
          variables: {
            where: {
              RecId: TariffRecIdRef
            }
          }
        });
        if (Tariff) {
          setData(Tariff);
        }
      } catch (err) {
        toast.error("Error getting Tariff.");
      } finally {
        setLoading(false);
      }
    };
    if (TariffRecIdRef) {
      fetchData();
    }
  }, [TariffRecIdRef, client]);
  return {
    data,
    loading
  };
};

export const useBookingCodeDetails = bookingCodeId => {
  const client = useApolloClient();
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const {
          data: { BookingCode = {} }
        } = await client.query({
          query: SINGLE_BOOKING_CODE_QUERY,
          variables: {
            where: {
              RecId: parseInt(bookingCodeId)
            }
          }
        });
        setData(BookingCode);
      } catch (err) {
        console.log(err.message);
      } finally {
        setLoading(false);
      }
    };
    if (bookingCodeId) {
      fetchData();
    }
  }, [bookingCodeId, client, data]);
  return { data, loading };
};

const ALL_SYSTEM_CODES_QUERY = gql`
  query SystemCodes($where: SystemCodeWhereInput!) {
    SystemCodes(
      where: $where
      orderByMulti: [ListOrder_ASC, CodeDescription_ASC]
    ) {
      RecId
      Type
      Category
      CodeName
      CodeId
      CodeDescription
    }
  }
`;

// where could be {Category, CodeName, CodeId, Category_in, CodeName_in, CodeId_in, etc}
export const useSystemCodes = where => {
  const client = useApolloClient();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const {
          data: { SystemCodes = {} }
        } = await client.query({
          query: ALL_SYSTEM_CODES_QUERY,
          variables: {
            where
          }
        });
        setData(SystemCodes);
      } catch (err) {
        console.log(err.message);
      } finally {
        setLoading(false);
      }
    };
    if (!data) {
      fetchData();
    }
  }, [client, data, where]);
  return { data, loading };
};

export const useMultipleSystemCodes = (Category, CodeName, CodeId_in) => {
  const client = useApolloClient();
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const {
          data: { SystemCodes = {} }
        } = await client.query({
          query: ALL_SYSTEM_CODES_QUERY,
          variables: {
            where: {
              Category,
              CodeName,
              CodeId_in
            }
          }
        });
        setData(SystemCodes);
      } catch (err) {
        console.log(err.message);
      } finally {
        setLoading(false);
      }
    };
    if (Category && CodeName) {
      fetchData();
    }
  }, [Category, CodeName, CodeId_in, client]);
  return { data, loading };
};
